/* eslint-disable @typescript-eslint/no-magic-numbers */

import { SHA256 } from '@/core/event_logic/utils/hash_utils';
import { Logger } from '@/shared/logger/logger';

import { FbUserData } from './types';

enum NormalizationRules {
  DateFormat = 'DateFormat',
  KeepOnlyDigits = 'KeepOnlyDigits',
  LowerCase = 'LowerCase',
  RemoveSpecialCharacters = 'RemoveSpecialCharacters',
  TrimAndLowerCase = 'TrimAndLowerCase',
  TwoLetterCode = 'TwoLetterCode',
}

const normalizationRules: Record<keyof FbUserData, NormalizationRules[]> = {
  country: [NormalizationRules.TwoLetterCode, NormalizationRules.LowerCase],
  ct: [
    NormalizationRules.TrimAndLowerCase,
    NormalizationRules.RemoveSpecialCharacters,
  ],
  db: [NormalizationRules.DateFormat],
  em: [NormalizationRules.TrimAndLowerCase],
  external_id: [],
  fn: [NormalizationRules.TrimAndLowerCase],
  ge: [NormalizationRules.LowerCase],
  ln: [NormalizationRules.TrimAndLowerCase],
  ph: [NormalizationRules.KeepOnlyDigits],
  st: [NormalizationRules.TwoLetterCode, NormalizationRules.LowerCase],
  zp: [NormalizationRules.KeepOnlyDigits],
};

const hashingRules: Record<keyof FbUserData, boolean> = {
  country: true,
  ct: true,
  db: true,
  em: true,
  external_id: true,
  fn: true,
  ge: true,
  ln: true,
  ph: true,
  st: true,
  zp: true,
};

const normalizationTransformer: Record<
  NormalizationRules,
  (input: string) => string
> = {
  [NormalizationRules.DateFormat]: (input: string) => {
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) {
      Logger.debug('Invalid date format for Meta normalization', { input });
      return '';
    }
    return date.toISOString().slice(0, 10).replaceAll('-', '');
  },
  [NormalizationRules.KeepOnlyDigits]: (input: string) =>
    input.replaceAll(/\D/g, ''),
  [NormalizationRules.LowerCase]: (input: string) => input.toLowerCase(),
  [NormalizationRules.RemoveSpecialCharacters]: (input: string) =>
    input.replaceAll(/[^\dA-Za-z]/g, ''),
  [NormalizationRules.TrimAndLowerCase]: (input: string) =>
    input.trim().toLowerCase(),
  [NormalizationRules.TwoLetterCode]: (input: string) => input.slice(0, 2),
};

export function normalizeUserData(userData: FbUserData): FbUserData {
  const normalizedData: FbUserData = { ...userData };

  for (const [key, values] of Object.entries(normalizedData)) {
    if (!Array.isArray(values)) continue;

    const rules = normalizationRules[key as keyof FbUserData];
    if (!rules) continue;

    normalizedData[key as keyof FbUserData] = values
      .map(value => {
        if (!value) return '';
        return rules.reduce(
          (acc, rule) => normalizationTransformer[rule](acc),
          value,
        );
      })
      .filter(Boolean); // Remove empty strings
  }

  return normalizedData;
}

export async function hashUserData(userData: FbUserData): Promise<FbUserData> {
  const hashedData: FbUserData = { ...userData };

  for (const [key, values] of Object.entries(hashedData)) {
    if (!Array.isArray(values)) continue;

    if (hashingRules[key as keyof FbUserData]) {
      hashedData[key as keyof FbUserData] = await Promise.all(
        values.map(async value => {
          if (!value) return '';
          return SHA256(value);
        }),
      );
    }
  }

  return hashedData;
}

export async function normalizeAndHashUserData(
  userData: FbUserData,
): Promise<FbUserData> {
  const normalizedData = normalizeUserData(userData);
  const hashedData = await hashUserData(normalizedData);
  return hashedData;
}
